import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getSupabase } from '@/sync/supabase'
import { useAuthStore } from '@/sync/auth'
import { useSettingsStore } from '@/stores/settings'

/**
 * Store de notificaciones in-app (F2.4).
 *
// Lee la tabla `notifications` (RLS: propia fila). Suscripción Realtime
// opcional para refrescar en vivo cuando llega una nueva.
 *
// Inserción: el cliente no inserta. La generan triggers server-side o
// RPCs security-definer (notify_user).
 */

export interface AppNotification {
  id: string
  type: string
  payload: Record<string, unknown>
  read: boolean
  createdAt: string
}

const TABLE = 'notifications'

export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<AppNotification[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const unreadCount = computed(() => items.value.filter((n) => !n.read).length)

  function client() {
    return getSupabase(useSettingsStore().settings.sync)
  }

  /** Carga las últimas 50 notificaciones del usuario. */
  async function load(): Promise<void> {
    const auth = useAuthStore()
    if (!auth.userId) return
    const c = client()
    if (!c) return
    loading.value = true
    error.value = null
    try {
      const { data, error: err } = await c
        .from(TABLE)
        .select('id, type, payload, read, created_at')
        .order('created_at', { ascending: false })
        .limit(50)
      if (err) throw err
      items.value = (data ?? []).map((r) => ({
        id: String(r.id),
        type: String(r.type),
        payload: (r.payload as Record<string, unknown>) ?? {},
        read: Boolean(r.read),
        createdAt: String(r.created_at),
      }))
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      loading.value = false
    }
  }

  /** Marca una notificación como leída. */
  async function markRead(id: string): Promise<void> {
    const c = client()
    if (!c) return
    const { error: err } = await c.from(TABLE).update({ read: true }).eq('id', id)
    if (err) return
    const n = items.value.find((x) => x.id === id)
    if (n) n.read = true
  }

  /** Marca todas como leídas. */
  async function markAllRead(): Promise<void> {
    const c = client()
    if (!c) return
    const { error: err } = await c
      .from(TABLE)
      .update({ read: true })
      .eq('read', false)
    if (err) return
    items.value = items.value.map((n) => ({ ...n, read: true }))
  }

  /**
   * Suscripción Realtime a nuevas notificaciones. Devuelve una función
   * de cleanup para cancelar la suscripción.
   */
  function subscribe(): () => void {
    const auth = useAuthStore()
    const c = client()
    if (!auth.userId || !c) return () => {}
    const channel = c
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: TABLE,
          filter: `user_id=eq.${auth.userId}`,
        },
        () => void load(),
      )
      .subscribe()
    return () => {
      c.removeChannel(channel)
    }
  }

  return { items, loading, error, unreadCount, load, markRead, markAllRead, subscribe }
})
